import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircle2, UserCheck, XCircle, Send, PieChart as PieChartIcon, BarChart3, Database, MessageSquare, Eye,
  Download, FileSpreadsheet, File as FilePdf, Users, FileText
} from "lucide-react";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { useToast } from "@/hooks/use-toast";
import { generateExport } from "@/utils/exportUtils";
import { loadAllInteractions, deleteInteraction, Interaction, fetchAllInteractionsFromServer, useInteractionsStore } from "@/data/interactionsStore";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { useSearchParams } from "react-router-dom";

export default function AdminEngagementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [partyFilter, setPartyFilter] = useState("all");
  const [confirmTransfer, setConfirmTransfer] = useState<{ id: string, party: string } | null>(null);
  
  const [allInteractions, setAllInteractions] = useState<Interaction[]>([]);
  const [interactionPartyFilter, setInteractionPartyFilter] = useState("all");
  const [interactionTypeFilter, setInteractionTypeFilter] = useState("all");
  const [interactionRegionFilter, setInteractionRegionFilter] = useState("all");

  const { interactions } = useInteractionsStore();

  useEffect(() => {
    fetchAllInteractionsFromServer().then(data => {
      setAllInteractions(data);
    });
  }, []);
  
  useEffect(() => {
    setAllInteractions(interactions);
  }, [interactions]);

  const handleValidateTransfer = async (id: string, partyAcronym: string) => {
    try {
      const res = await fetch("https://kaynin.ma/backend/api/interactions.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_status", id, status: "transferred" })
      });
      if (res.ok) {
        setAllInteractions(prev => prev.map(i => i.id === id ? { ...i, status: "transferred" } : i));
        toast({ title: `Transfert Réussi & Email Généré`, description: `L'engagement a été validé et assigné au parti ${partyAcronym}.`, duration: 5000 });
      }
    } catch(e) { console.error(e); }
    setConfirmTransfer(null);
  };

  const handleReject = async (id: string) => {
    try {
      const res = await fetch("https://kaynin.ma/backend/api/interactions.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_status", id, status: "rejected" })
      });
      if (res.ok) {
        setAllInteractions(prev => prev.map(i => i.id === id ? { ...i, status: "rejected" } : i));
        toast({ title: "Demande Rejetée", description: "Le citoyen sera averti.", variant: "destructive" });
      }
    } catch(e) { console.error(e); }
  };

  const engagements = useMemo(() => {
    return allInteractions.filter(i => i.type === "engagement").map(i => ({
      id: i.id,
      firstName: i.senderFirstName,
      lastName: i.senderLastName,
      genre: i.genre || "homme",
      age: i.age || "Non spécifié",
      profession: i.profession || "Non spécifié",
      email: i.senderEmail,
      phone: i.senderPhone,
      region: i.region,
      province: i.province,
      object: i.subject,
      message: i.message,
      party: i.partyAcronym,
      status: i.status === "nouveau" ? "pending" : i.status
    }));
  }, [allInteractions]);

  const filteredEngagements = useMemo(() => {
    if (partyFilter === "all") return engagements;
    return engagements.filter(e => e.party === partyFilter);
  }, [engagements, partyFilter]);

  // --- STATS COMPUTATIONS ---

  const genderStats = useMemo(() => {
    const h = engagements.filter(e => e.genre === "homme").length;
    const f = engagements.filter(e => e.genre === "femme").length;
    return [
      { name: "Homme", value: h, fill: "hsl(var(--primary))" },
      { name: "Femme", value: f, fill: "hsl(var(--accent))" }
    ];
  }, [engagements]);

  const ageStats = useMemo(() => {
    const ageGroups = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
    return ageGroups.map(ag => ({
      name: ag,
      value: engagements.filter(e => e.age === ag).length
    }));
  }, [engagements]);

  const professionStats = useMemo(() => {
    const counts = engagements.reduce((acc, curr) => {
      acc[curr.profession] = (acc[curr.profession] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [engagements]);

  const regionStats = useMemo(() => {
    const counts = engagements.reduce((acc, curr) => {
      acc[curr.region] = (acc[curr.region] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [engagements]);

  const filteredInteractions = useMemo(() => {
    return allInteractions.filter(i => {
      if (i.type === "engagement") return false; // engagements are shown in the other tab
      if (interactionPartyFilter !== "all" && i.partyAcronym !== interactionPartyFilter) return false;
      if (interactionTypeFilter !== "all" && i.type !== interactionTypeFilter) return false;
      if (interactionRegionFilter !== "all" && i.region !== interactionRegionFilter) return false;
      return true;
    });
  }, [allInteractions, interactionPartyFilter, interactionTypeFilter, interactionRegionFilter]);

  const candidateStatsArray = useMemo(() => {
    const candidateInteractions = filteredInteractions.filter(i => i.type === "candidate");
    const statsPerCandidate = candidateInteractions.reduce((acc, curr) => {
      acc[curr.candidateName] = (acc[curr.candidateName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(statsPerCandidate)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredInteractions]);

  // --- EXPORT FUNCTIONS ---
  const exportData = (data: any[], filename: string, format: 'excel' | 'word' | 'pdf') => {
    if (!data.length) {
      toast({ title: "Vide", description: "Aucune donnée à exporter.", variant: "destructive" });
      return;
    }
    toast({ title: "Export", description: `Génération export ${format.toUpperCase()} en cours...` });
    
    let processedData = data;
    if (data[0] && data[0].firstName) {
       processedData = data.map(e => ({
          Nom: `${e.firstName} ${e.lastName}`,
          Email: e.email,
          Telephone: e.phone,
          Age: e.age,
          Profession: e.profession,
          Parti: e.party,
          Statut: e.status
       }));
    }

    generateExport(processedData, filename, `Export ${filename}`, format);
  };

  const ExportButton = ({ data, filename }: { data: any[], filename: string }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 shrink-0">
          <Download className="w-4 h-4" /> Exporter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-10 w-48">
        <DropdownMenuItem onClick={() => exportData(data, filename, 'excel')} className="cursor-pointer">
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" /> Excel (.csv)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportData(data, filename, 'word')} className="cursor-pointer">
          <FileText className="w-4 h-4 mr-2 text-blue-600" /> Word (.doc)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportData(data, filename, 'pdf')} className="cursor-pointer">
          <FilePdf className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-xl md:text-3xl font-display font-bold text-foreground">Engagements &amp; Statistiques</h1>
          <p className="text-muted-foreground font-body mt-1 text-sm">Supervisez la participation citoyenne et les adhésions aux partis.</p>
        </div>
        <ExportButton data={filteredEngagements} filename="Rapport_Engagements_Kaynin" />
      </div>

      <Tabs value={searchParams.get("tab") || "demandes"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="mb-6 h-auto bg-muted/50 p-1 flex flex-wrap gap-1 max-w-3xl rounded-xl overflow-x-auto">
          <TabsTrigger value="demandes" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <UserCheck className="w-4 h-4" /> Engagements Partis
          </TabsTrigger>
          <TabsTrigger value="interactions" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Messages Candidats
          </TabsTrigger>
          <TabsTrigger value="stats" className="rounded-lg flex-1 font-semibold flex items-center gap-2">
            <PieChartIcon className="w-4 h-4" /> Statistiques
          </TabsTrigger>
        </TabsList>

        {/* DEMANDES TABS */}
        <TabsContent value="demandes" className="mt-0 space-y-4">
          <Card className="border-border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-border/50">
              <div>
                <CardTitle className="text-xl font-display">Demandes "Je m'engage"</CardTitle>
                <CardDescription>Citoyens souhaitant adhérer à un p. politique.</CardDescription>
              </div>
              <Select value={partyFilter} onValueChange={setPartyFilter}>
                <SelectTrigger className="w-44 bg-background"><SelectValue placeholder="Filtrer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les Partis</SelectItem>
                  {allPartiesIncludingNonParliamentary.map(p => <SelectItem key={p.acronym} value={p.acronym}>{p.acronym}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm font-body">
                  <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                    <tr>
                      <th className="py-4 px-6 text-left">Citoyen (Genre/Âge)</th>
                      <th className="py-4 px-4 text-left">Contact & Localisation</th>
                      <th className="py-4 px-4 text-left w-64">Objet & Message</th>
                      <th className="py-4 px-4 text-left">Parti</th>
                      <th className="py-4 px-4 text-left">Statut</th>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {filteredEngagements.map((eng) => (
                      <tr key={eng.id} className="hover:bg-muted/10 transition">
                        <td className="py-3 px-6">
                          <div className="font-bold text-foreground">{eng.firstName} {eng.lastName}</div>
                          <div className="text-xs text-muted-foreground capitalize">{eng.genre} &bull; {eng.age || 'N/A'}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-foreground text-xs">{eng.email} | {eng.phone}</div>
                          <div className="text-xs text-muted-foreground mt-1">{eng.province || eng.region}</div>
                          <div className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 mt-1 rounded-sm inline-block">{eng.profession}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-xs truncate max-w-[200px]">{eng.object}</div>
                          {eng.message && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 px-1 mt-1 text-primary hover:bg-primary/10">
                                  <MessageSquare className="w-3 h-3" /> Lire Message
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-[425px]">
                                <DialogHeader>
                                  <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Message Citoyen</DialogTitle>
                                  <DialogDescription>Objet: <span className="font-bold text-foreground">{eng.object}</span></DialogDescription>
                                </DialogHeader>
                                <div className="p-4 bg-muted/30 rounded-xl mt-2 text-sm">"{eng.message}"</div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </td>
                        <td className="py-3 px-4"><Badge variant="outline">{eng.party}</Badge></td>
                        <td className="py-3 px-4">
                          {eng.status === 'pending' && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-none">En attente</Badge>}
                          {(eng.status === 'transferred' || eng.status === 'traite') && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none">Transféré</Badge>}
                          {eng.status === 'rejected' && <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10 border-none">Rejeté</Badge>}
                        </td>
                        <td className="py-3 px-6 text-right">
                          <div className="flex justify-end gap-2">
                            {eng.status === 'pending' ? (
                              <>
                                <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5 px-2" onClick={() => handleReject(eng.id)}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" className="bg-primary hover:bg-primary/90 gap-1.5" onClick={() => setConfirmTransfer({ id: eng.id, party: eng.party })}>
                                  <Send className="w-3 h-3" /> Valider
                                </Button>
                              </>
                            ) : (eng.status === 'transferred' || eng.status === 'traite') ? (
                               <Button size="sm" variant="secondary" className="gap-1.5 opacity-70" disabled><CheckCircle2 className="w-3.5 h-3.5" /></Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredEngagements.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-muted-foreground text-sm">
                          Aucun engagement trouvé.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTERACTIONS CANDIDATS TABS */}
        <TabsContent value="interactions" className="mt-0 space-y-4">
          <Card className="border-border shadow-sm">
            <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between pb-4 border-b border-border/50 gap-4">
              <div>
                <CardTitle className="text-xl font-display">Interactions & Notifications</CardTitle>
                <CardDescription>Supervision globale des interactions (Partis et Candidats).</CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={interactionTypeFilter} onValueChange={setInteractionTypeFilter}>
                  <SelectTrigger className="w-36 bg-background"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous types</SelectItem>
                    <SelectItem value="party">Messages Parti</SelectItem>
                    <SelectItem value="candidate">Notifications Candidats</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={interactionPartyFilter} onValueChange={setInteractionPartyFilter}>
                  <SelectTrigger className="w-36 bg-background"><SelectValue placeholder="Parti" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les Partis</SelectItem>
                    {allPartiesIncludingNonParliamentary.map(p => <SelectItem key={p.acronym} value={p.acronym}>{p.acronym}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={interactionRegionFilter} onValueChange={setInteractionRegionFilter}>
                  <SelectTrigger className="w-36 bg-background"><SelectValue placeholder="Région" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes Régions</SelectItem>
                    {Array.from(new Set(allInteractions.map(i => i.region))).map(r => r ? <SelectItem key={r} value={r}>{r}</SelectItem> : null)}
                  </SelectContent>
                </Select>
                <Button onClick={() => exportData(filteredInteractions.map(i => ({
                  Date: new Date(i.date).toLocaleDateString(),
                  Type: i.type === "candidate" ? "Candidat" : "Parti",
                  Cible: i.candidateName,
                  Parti: i.partyAcronym,
                  Région: i.region,
                  Expéditeur: `${i.senderFirstName} ${i.senderLastName}`,
                  Email: i.type === "candidate" ? "Masqué" : i.senderEmail,
                  Téléphone: i.type === "candidate" ? "Masqué" : i.senderPhone,
                  Sujet: i.type === "candidate" ? "Masqué" : i.subject,
                  Message: i.type === "candidate" ? "Masqué" : i.message,
                })), "Interactions_Admin", "excel")} variant="outline" size="sm" className="gap-2 shrink-0 h-10">
                  <Download className="w-4 h-4" /> Exporter (.xlsx)
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              
              {/* Stats Candidates Admin */}
              {candidateStatsArray.length > 0 && interactionTypeFilter !== "party" && (
                <div className="bg-muted/10 p-4 border-b border-border/50">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-primary" /> Palmarès des candidats les plus sollicités
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {candidateStatsArray.slice(0, 10).map((stat, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1 font-medium bg-background border-border shadow-sm text-xs">
                        {stat.name} <span className="ml-2 text-primary font-bold">{stat.count} msg</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm font-body">
                  <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                    <tr>
                      <th className="py-4 px-6 text-left">Date & Type</th>
                      <th className="py-4 px-4 text-left">Cible & Parti</th>
                      <th className="py-4 px-4 text-left">Citoyen & Contact</th>
                      <th className="py-4 px-4 text-left w-64">Objet & Message</th>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {filteredInteractions.map((inter) => (
                      <tr key={inter.id} className="hover:bg-muted/10 transition">
                        <td className="py-3 px-6 text-xs">
                          <div className="font-semibold text-foreground mb-1">
                            {inter.type === "candidate" ? <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">Candidat</Badge> : <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">Parti</Badge>}
                          </div>
                          {new Date(inter.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-foreground">{inter.candidateName}</div>
                          <Badge variant="outline" className="mt-1">{inter.partyAcronym}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-foreground text-xs">{inter.senderFirstName} {inter.senderLastName}</div>
                          <div className="text-muted-foreground text-xs truncate max-w-[150px]">{inter.region} / {inter.province}</div>
                          {inter.type !== "candidate" && (
                            <>
                              <div className="text-muted-foreground text-xs">{inter.senderEmail}</div>
                              <div className="text-muted-foreground text-xs">{inter.senderPhone}</div>
                            </>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {inter.type === "candidate" ? (
                            <div className="text-xs text-muted-foreground italic flex items-center gap-1">
                              <Eye className="w-3 h-3" /> Contenu masqué (Confidentialité)
                            </div>
                          ) : (
                            <>
                              <div className="font-semibold text-xs truncate max-w-[200px]">{inter.subject}</div>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 px-1 mt-1 text-primary hover:bg-primary/10">
                                    <MessageSquare className="w-3 h-3" /> Lire Message
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[425px]">
                                  <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Message à {inter.candidateName}</DialogTitle>
                                    <DialogDescription>De: {inter.senderFirstName} {inter.senderLastName}</DialogDescription>
                                  </DialogHeader>
                                  <div className="bg-muted/30 p-2 rounded text-xs font-semibold">Objet: {inter.subject}</div>
                                  <div className="p-4 bg-muted/30 rounded-xl text-sm mt-2">"{inter.message}"</div>
                                </DialogContent>
                              </Dialog>
                            </>
                          )}
                        </td>
                        <td className="py-3 px-6 text-right">
                          <Button size="sm" variant="ghost" className="text-destructive border border-transparent hover:border-destructive/30 hover:bg-destructive/10 px-2" onClick={() => {
                            deleteInteraction(inter.id);
                            fetchAllInteractionsFromServer().then(setAllInteractions);
                          }}>
                            <XCircle className="w-4 h-4" /> Supprimer
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredInteractions.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-muted-foreground text-sm">
                          Aucune interaction trouvée avec les filtres actuels.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STATS GLOBALES TABS */}
        <TabsContent value="stats" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

            <Card className="border-border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-display flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-blue-500" /> Profil : Âge</CardTitle>
                </div>
                <ExportButton data={ageStats} filename="Kaynin_Stats_Age" />
              </CardHeader>
              <CardContent>
                <div className="h-[250px] w-full mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ageStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
                      <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted">
                      <tr><th className="p-2 font-semibold">Tranche d'âge</th><th className="p-2 font-semibold text-right">Membres Engagés</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {ageStats.map(s => (
                        <tr key={s.name}><td className="p-2">{s.name}</td><td className="p-2 text-right font-bold">{s.value}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-display flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-orange-500" /> Profil : Régions & Provinces</CardTitle>
                </div>
                <ExportButton data={regionStats} filename="Kaynin_Stats_Regions" />
              </CardHeader>
              <CardContent>
                <div className="h-[250px] w-full mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={regionStats} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                        {regionStats.map((e, index) => <Cell key={index} fill={`hsl(var(--primary) / ${0.9 - index*0.1})`} />)}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted">
                      <tr><th className="p-2 font-semibold">Région</th><th className="p-2 font-semibold text-right">Citoyens</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {regionStats.map(s => (
                        <tr key={s.name}><td className="p-2 truncate max-w-[150px]">{s.name}</td><td className="p-2 text-right font-bold">{s.value}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle className="text-lg font-display flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-emerald-500" /> Profil : Professions</CardTitle></div>
                <ExportButton data={professionStats} filename="Kaynin_Stats_Professions" />
              </CardHeader>
              <CardContent>
                <div className="h-[250px] w-full mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={professionStats} cx="50%" cy="50%" outerRadius={90} paddingAngle={2} dataKey="value">
                        {professionStats.map((e, index) => <Cell key={index} fill={`hsl(var(--accent) / ${0.9 - index*0.15})`} />)}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-muted">
                      <tr><th className="p-2 font-semibold">Secteur / Profession</th><th className="p-2 font-semibold text-right">Demandes</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {professionStats.map(s => (
                        <tr key={s.name}><td className="p-2 capitalize">{s.name}</td><td className="p-2 text-right font-bold">{s.value}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

          </div>
        </TabsContent>
      </Tabs>

      {/* Confirmation de transfert AlertDialog */}
      {confirmTransfer && (
        <AlertDialog open={!!confirmTransfer} onOpenChange={(open) => !open && setConfirmTransfer(null)}>
          <AlertDialogContent className="rounded-3xl border-none shadow-2xl sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-xl text-foreground">
                Confirmer le transfert
              </AlertDialogTitle>
              <AlertDialogDescription className="font-body text-muted-foreground mt-2 text-base">
                Vous êtes sur le point de valider cet engagement et de le transférer au parti <strong className="text-foreground">{confirmTransfer.party}</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="bg-primary/5 p-4 rounded-xl border border-primary/20 my-2">
              <p className="text-sm font-semibold text-primary mb-1">Vérification de l'email de destination :</p>
              <p className="text-foreground font-body bg-background px-3 py-2 rounded-lg border border-border shadow-sm inline-block">
                contact@{confirmTransfer.party.toLowerCase()}.ma
              </p>
            </div>
            <AlertDialogFooter className="mt-4 gap-2">
              <AlertDialogCancel className="rounded-xl h-11">Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleValidateTransfer(confirmTransfer.id, confirmTransfer.party)}
                className="rounded-xl bg-primary hover:bg-primary/90 h-11 gap-2 border-none shadow-md"
              >
                <Send className="w-4 h-4" /> Confirmer & Envoyer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
